import React, { useEffect, useState } from 'react';
import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/lib/auth';
import { CartProvider } from '@/lib/cart';
import { BranchProvider } from '@/lib/branch';
import { PolicyProvider } from '@/lib/policy';
import { RequireAuth } from '@/components/layout/RequireAuth';
import Landing from '@/pages/Landing';
import LoginPage from '@/pages/Login';
import SignupPage from '@/pages/Signup';
import MenuPage from '@/pages/Menu';
import CheckoutPage from '@/pages/Checkout';
import OrderTracking from '@/pages/OrderTracking';
import MyOrders from '@/pages/MyOrders';
import MyProfile from '@/pages/MyProfile';
import Profile from '@/pages/Profile';
import KDS from '@/pages/KDS';
import AdminDashboard from '@/pages/Admin';
import POS from '@/pages/POS';
import TableMode from '@/pages/TableMode';
import WaiterMode from '@/pages/WaiterMode';
import ServerConsole from '@/pages/ServerConsole';
import LoyaltyPage from '@/pages/Loyalty';
import InventoryPage from '@/pages/Inventory';
import StaffPage from '@/pages/Staff';
import BranchesPage from '@/pages/Branches';
import ReportsPage from '@/pages/Reports';
import FeedbackAdminPage from '@/pages/FeedbackAdmin';
import MarketingPage from '@/pages/Marketing';
import AuditPage from '@/pages/Audit';
import CheckinKiosk from '@/pages/CheckinKiosk';
import MobileMoneyPay from '@/pages/MobileMoneyPay';
import DriversPage from '@/pages/Drivers';
import DispatchAcceptPage from '@/pages/DispatchAccept';
import PublicFeedbackPage from '@/pages/PublicFeedback';
import '@/App.css';

function CartRedirect() {
  return <Navigate to="/checkout" replace />;
}

/**
 * Route `/profile` to the right component based on the signed-in user's role.
 * Customers see the consumer-facing Profile (with addresses, loyalty, delete
 * account), staff see MyProfile (kiosk PIN management). Without this, the
 * react-router `<Route>` ordering would always pick the first registered
 * match, hiding the customer profile from real customers.
 */
function ProfileRouter() {
  const { user } = useAuth();
  const STAFF = ['kitchen', 'cashier', 'driver', 'admin', 'owner', 'waiter'];
  if (user && STAFF.includes(user.role)) return <MyProfile />;
  return <Profile />;
}

function App() {
  return (
    <AuthProvider>
      <BranchProvider>
        <PolicyProvider>
          <CartProvider>
            <BrowserRouter>
            <Toaster richColors closeButton position="top-right" />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/cart" element={<CartRedirect />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/order/:orderId" element={<OrderTracking />} />
            <Route path="/orders" element={<RequireAuth><MyOrders /></RequireAuth>} />
            <Route path="/profile" element={<RequireAuth><ProfileRouter /></RequireAuth>} />
            <Route path="/staff-profile" element={<RequireAuth><MyProfile /></RequireAuth>} />
            <Route path="/kds" element={<RequireAuth roles={['kitchen', 'admin', 'owner']}><KDS /></RequireAuth>} />
            <Route path="/admin" element={<RequireAuth roles={['admin', 'owner']}><AdminDashboard /></RequireAuth>} />
            <Route path="/server" element={<RequireAuth roles={['admin', 'owner']}><ServerConsole /></RequireAuth>} />
            <Route path="/pos" element={<RequireAuth roles={['cashier', 'admin', 'owner']}><POS /></RequireAuth>} />
            <Route path="/waiter" element={<RequireAuth roles={['waiter', 'admin', 'owner']}><WaiterMode /></RequireAuth>} />
            <Route path="/driver" element={<Navigate to="/drivers" replace />} />
            <Route path="/drivers-admin" element={<Navigate to="/drivers?tab=registry" replace />} />
            <Route path="/drivers" element={<RequireAuth roles={['driver', 'admin', 'owner']}><DriversPage /></RequireAuth>} />
            <Route path="/loyalty" element={<RequireAuth><LoyaltyPage /></RequireAuth>} />
            <Route path="/inventory" element={<RequireAuth roles={['admin', 'owner']}><InventoryPage /></RequireAuth>} />
            <Route path="/staff" element={<RequireAuth roles={['admin', 'owner']}><StaffPage /></RequireAuth>} />
            <Route path="/branches-admin" element={<RequireAuth roles={['admin', 'owner']}><BranchesPage /></RequireAuth>} />
            <Route path="/drivers-admin-legacy" element={<RequireAuth roles={['admin', 'owner']}><DriversPage /></RequireAuth>} />
            <Route path="/reports" element={<RequireAuth roles={['admin', 'owner', 'cashier']}><ReportsPage /></RequireAuth>} />
            <Route path="/feedback-admin" element={<RequireAuth roles={['admin', 'owner', 'cashier']}><FeedbackAdminPage /></RequireAuth>} />
            <Route path="/feedback" element={<PublicFeedbackPage />} />
            <Route path="/dispatch/:token" element={<DispatchAcceptPage />} />
            <Route path="/marketing" element={<RequireAuth roles={['admin', 'owner']}><MarketingPage /></RequireAuth>} />
            <Route path="/audit" element={<RequireAuth roles={['admin', 'owner']}><AuditPage /></RequireAuth>}/>
            <Route path="/checkin" element={<CheckinKiosk />} />
            <Route path="/pay/mm/:token" element={<MobileMoneyPay />} />
            <Route path="/table/:tableId" element={<TableMode />} />
            <Route path="/t/:qrToken" element={<QrTableEntry />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </BrowserRouter>
          </CartProvider>
        </PolicyProvider>
      </BranchProvider>
    </AuthProvider>
  );
}

function QrTableEntry() {
  // Customer scans QR -> we redirect them to /menu with table query param
  const path = window.location.pathname;
  const qr = path.split('/').pop();
  return <Navigate to={`/menu?table=${qr}`} replace />;
}

export default App;
